import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, ImageIcon } from "lucide-react";
import { ZoomableImage } from "components/common/ZoomableImage";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  formatMoney,
  getCampaignRouteId,
  getInvestmentThumbAlt,
  getInvestmentThumbUrl,
  getProjectTitle,
  humanizeStatus,
} from "lib/investmentListHelpers";
import {
  CANCELLATION_REQUESTED_HINT,
  canRequestInvestmentCancellation,
  isCancellationRequestRequested,
} from "lib/investmentCancellation";

function InvestmentProjectThumb({ investment }) {
  const url = getInvestmentThumbUrl(investment);
  const alt = getInvestmentThumbAlt(investment);

  if (!url) {
    return (
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground"
        aria-hidden
      >
        <ImageIcon className="h-6 w-6" />
      </div>
    );
  }

  return (
    <ZoomableImage
      src={url}
      alt={alt}
      variant="lightbox"
      className="rounded-lg object-cover"
      containerClassName="h-12 w-12 shrink-0"
    />
  );
}

InvestmentProjectThumb.propTypes = {
  investment: PropTypes.object.isRequired,
};

function InvestmentStatusBadge({ paymentStatus, label }) {
  if (paymentStatus === "paid") {
    return (
      <Badge className="shrink-0 bg-accent text-accent-foreground">{label}</Badge>
    );
  }
  if (paymentStatus === "failed") {
    return (
      <Badge variant="destructive" className="shrink-0">
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="shrink-0">
      {label}
    </Badge>
  );
}

InvestmentStatusBadge.propTypes = {
  paymentStatus: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

function InvestmentChangeDisplay({ changeRaw, projectStatus }) {
  const hasChange =
    changeRaw !== null &&
    changeRaw !== undefined &&
    changeRaw !== "" &&
    Number.isFinite(Number(changeRaw));

  if (hasChange) {
    const isPositive = Number(changeRaw) >= 0;
    const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;
    const toneClass = isPositive ? "text-accent" : "text-destructive";
    const prefix = isPositive ? "+" : "";

    return (
      <p className={`text-sm flex items-center justify-end gap-1 ${toneClass}`}>
        <TrendIcon className="h-3 w-3" />
        {prefix}
        {changeRaw}%
      </p>
    );
  }

  if (!projectStatus) return null;

  return (
    <p className="text-xs text-muted-foreground capitalize">
      {humanizeStatus(projectStatus)}
    </p>
  );
}

InvestmentChangeDisplay.propTypes = {
  changeRaw: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  projectStatus: PropTypes.string,
};

InvestmentChangeDisplay.defaultProps = {
  changeRaw: null,
  projectStatus: null,
};

function CancellationRequestedHint() {
  return (
    <Badge variant="secondary" className="shrink-0 font-normal">
      {CANCELLATION_REQUESTED_HINT}
    </Badge>
  );
}

function InvestmentRowDetails({ investment, title, badge, showCancellationRequestedHint }) {
  const investedAt =
    investment.createdAt ?? investment.dateInvested ?? investment.updatedAt;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-medium truncate">{title}</h4>
        {badge}
        {showCancellationRequestedHint ? <CancellationRequestedHint /> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
        <span>{investment.shares ?? 0} shares</span>
        {investedAt ? (
          <>
            <span className="hidden sm:inline">•</span>
            <span>
              Invested{" "}
              {new Date(investedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

InvestmentRowDetails.propTypes = {
  investment: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  badge: PropTypes.node.isRequired,
  showCancellationRequestedHint: PropTypes.bool,
};

InvestmentRowDetails.defaultProps = {
  showCancellationRequestedHint: false,
};

export function InvestmentListRow({ investment, tab, onRequestCancel }) {
  const campaignId = getCampaignRouteId(investment);
  const title = getProjectTitle(investment);
  const amount = investment.amount ?? investment.currentValue ?? 0;
  const currency = investment.currency ?? "USD";
  const changeRaw =
    investment.change ?? investment.gainPercent ?? investment.gainPercentage;

  const statusBadgeLabel =
    humanizeStatus(investment.status) ||
    humanizeStatus(investment.project?.status) ||
    "—";
  const paymentStatus = String(investment.status ?? "").toLowerCase();

  const showCancellationRequestedHint = isCancellationRequestRequested(investment);

  const showCancelButton =
    typeof onRequestCancel === "function" && canRequestInvestmentCancellation(investment);

  const handleCancelClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRequestCancel(investment);
  };

  return (
    <div data-tab={tab} className="flex items-stretch gap-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
      <Link to={`/campaign/${campaignId}`} className="flex min-w-0 flex-1 items-center gap-4 p-4">
        <InvestmentProjectThumb investment={investment} />
        <InvestmentRowDetails
          investment={investment}
          title={title}
          showCancellationRequestedHint={showCancellationRequestedHint}
          badge={
            <InvestmentStatusBadge
              paymentStatus={paymentStatus}
              label={statusBadgeLabel}
            />
          }
        />
        <div className="text-right shrink-0">
          <p className="font-semibold">{formatMoney(amount, currency)}</p>
          <InvestmentChangeDisplay
            changeRaw={changeRaw}
            projectStatus={investment.project?.status}
          />
        </div>
      </Link>

      {showCancelButton ? (
        <div className="flex shrink-0 items-center border-l px-3 py-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  );
}

InvestmentListRow.propTypes = {
  investment: PropTypes.object.isRequired,
  tab: PropTypes.oneOf(["all", "pending", "paid", "failed", "cancelled"]).isRequired,
  onRequestCancel: PropTypes.func,
};

InvestmentListRow.defaultProps = {
  onRequestCancel: undefined,
};
